import React, { useEffect, useState } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Badge, Typography, Input, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  ShopOutlined,
  UserOutlined,
  LaptopOutlined,
  BarChartOutlined,
  SettingOutlined,
  PhoneOutlined,
  DesktopOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  StarOutlined,
  FolderOutlined,
  SearchOutlined,
  MessageOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from 'react-query';
import api from '../config/api';
import NotificationBell from './NotificationBell';

const MOBILE_BREAKPOINT = 768;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface LayoutProps {
  children: React.ReactNode;
}

const LayoutComponent: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const isChatPage = location.pathname === '/chat';
  const hideAppSidebarOnMobile = isMobile && isChatPage;
  const { Search } = Input;
  
  // Set page title
  useEffect(() => {
    document.title = 'Vitreous Support - Support Ticket Platform';
  }, []);

  // Get search query from URL if on search page
  const [searchQuery, setSearchQuery] = useState('');
  
  // Update search query when URL changes
  useEffect(() => {
    if (location.pathname === '/search') {
      const searchParams = new URLSearchParams(location.search);
      const query = searchParams.get('q') || '';
      setSearchQuery(query);
    } else {
      setSearchQuery('');
    }
  }, [location.pathname, location.search]);

  // Fetch version info with cache busting using buildNumber
  const { data: versionInfo, refetch: refetchVersion } = useQuery(
    ['version'],
    async () => {
      const res = await api.get('/api/version', {
        params: { _cb: Date.now() } // Cache buster
      });
      return res.data;
    },
    { 
      staleTime: 1 * 60 * 1000, // 1 minute - allows version to update quickly
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Refetch when component mounts
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      cacheTime: 2 * 60 * 1000 // Keep in cache for 2 minutes
    }
  );

  // Temporarily disable ticket counts query to prevent refresh loops
  // const { data: ticketCounts } = useQuery(
  //   ['ticket-counts'],
  //   async () => {
  //     const res = await api.get('/api/tickets/counts');
  //     return res.data;
  //   },
  //   { staleTime: 30_000, refetchInterval: 60_000 }
  // );
  const ticketCounts = { all: 0, open: 0, pending: 0, solved: 0, mine: 0, unassigned: 0 };

  const isAgentOrSales = user?.role === 'admin' || user?.role === 'agent' || user?.role === 'developer' || user?.role === 'sales_rep_internal';
  const { data: callMeCountData } = useQuery(
    'call-me-count',
    () => api.get('/api/call-me/count').then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales, retry: false, refetchInterval: 60000 }
  );
  const callMeCount = callMeCountData?.count ?? 0;

  // Vitreous Support logo - stable URL so it doesn't re-request on every render (avoids 503 flash and "VS" fallback)
  const [logoError, setLogoError] = useState(false);
  const basePath = process.env.PUBLIC_URL || '';
  // Stable cache buster: only change when build actually changes (no Date.now() to avoid refresh loop)
  const logoCacheBuster = versionInfo?.buildNumber ?? '1';
  const logoFileName = 'logo.png';
  const logoUrl = `${basePath}/${logoFileName}?v=${logoCacheBuster}`.replace('//', '/');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGlobalSearch = (value: string) => {
    if (value.trim()) {
      navigate(`/search?q=${encodeURIComponent(value.trim())}`);
      // Don't clear searchQuery - let useEffect handle it from URL
    }
  };

  // Inject Jotform Agent script after login (not on login page)
  useEffect(() => {
    // Only inject if user is logged in and not on login/reset password/chat pages (chat has its own layout)
    if (!user || location.pathname === '/login' || location.pathname === '/reset-password' || location.pathname === '/chat') {
      // Remove script if user is not logged in or on login page
      const existing = document.querySelector('script[data-jf-agent="true"]') as HTMLScriptElement | null;
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
        // Also remove any Jotform iframe/widgets that might have been created
        const jotformWidgets = document.querySelectorAll('[id^="jf-"], [class^="jf-"]');
        jotformWidgets.forEach(widget => {
          if (widget.parentNode) {
            widget.parentNode.removeChild(widget);
          }
        });
      }
      return;
    }

    // Check if script already exists
    const existing = document.querySelector('script[data-jf-agent="true"]') as HTMLScriptElement | null;
    if (existing) return;

    // Inject Jotform Agent script
    const s = document.createElement('script');
    s.src = 'https://cdn.jotfor.ms/agent/embedjs/01973cd0ee467fc6a0f9dd4559fb817a84df/embed.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-jf-agent', 'true');
    document.body.appendChild(s);

    // Cleanup function
    return () => {
      const el = document.querySelector('script[data-jf-agent="true"]') as HTMLScriptElement | null;
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      // Also remove any Jotform widgets
      const jotformWidgets = document.querySelectorAll('[id^="jf-"], [class^="jf-"]');
      jotformWidgets.forEach(widget => {
        if (widget.parentNode) {
          widget.parentNode.removeChild(widget);
        }
      });
    };
  }, [user, location.pathname]);

  // Profile dropdown menu
  const profileMenu: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserSwitchOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  // Navigation menu items
  const menuItems: MenuProps['items'] = [
    // Always include these items for all users
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: '/tickets',
      icon: <FileTextOutlined />,
      label: 'Tickets',
      // Do NOT navigate on parent when children exist; rely on submenu items
      children: [
        {
          key: '/tickets?tab=all',
          label: (
            <span>
              All Tickets
              {typeof ticketCounts?.all === 'number' && (
                <Badge count={ticketCounts.all} style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=all'),
        },
        {
          key: '/tickets?tab=callme',
          label: (
            <span>
              Call Me
              {callMeCount > 0 && (
                <Badge count={callMeCount} style={{ marginLeft: 8, backgroundColor: '#faad14' }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=callme'),
        },
        {
          key: '/tickets?tab=my',
          label: (
            <span>
              My Tickets
              {typeof ticketCounts?.mine === 'number' && (
                <Badge count={ticketCounts.mine} style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=my'),
        },
        {
          key: '/tickets?tab=unassigned',
          label: (
            <span>
              Unassigned
              {typeof ticketCounts?.unassigned === 'number' && (
                <Badge count={ticketCounts.unassigned} style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
          onClick: () => navigate('/tickets?tab=unassigned'),
        },
      ],
    },
    ...(user?.role === 'admin' || user?.role === 'agent'
      ? [
          {
            key: '/replacements',
            icon: <ToolOutlined />,
            label: 'Replacement',
            onClick: () => navigate('/equipment?tab=replacements'),
          },
        ]
      : []),
    {
      key: '/businesses',
      icon: <ShopOutlined />,
      label: 'Businesses',
      onClick: () => navigate('/businesses'),
    },
    ...(user?.role === 'sales' || user?.role === 'sales_rep_internal'
      ? [
          {
            key: '/salesportal',
            icon: <FolderOutlined />,
            label: 'Sales Portal',
            onClick: () => navigate('/salesportal'),
          },
        ]
      : []),
    ...(user?.role === 'admin'
      ? [
          {
            key: '/users',
            icon: <UserOutlined />,
            label: 'Users',
            onClick: () => navigate('/users'),
          },
          {
            key: '/salesportal',
            icon: <FolderOutlined />,
            label: 'Sales Agent Portal',
            onClick: () => navigate('/salesportal'),
          },
          {
            key: '/analytics',
            icon: <BarChartOutlined />,
            label: 'Analytics',
            onClick: () => navigate('/analytics'),
          },
          {
            key: '/feedback-dashboard',
            icon: <StarOutlined />,
            label: 'Feedback',
            onClick: () => navigate('/feedback-dashboard'),
          },
          {
            key: '/settings',
            icon: <SettingOutlined />,
            label: 'Settings',
            onClick: () => navigate('/settings'),
          },
          {
            key: '/audit-logs',
            icon: <FileTextOutlined />,
            label: 'Audit Logs',
            onClick: () => navigate('/audit-logs'),
          },
        ]
      : []),
    // Call Center, Calls, and Remote Access hidden
  ];
  
  // Get selected menu key based on current path
  const getSelectedKey = () => {
    const path = location.pathname;
    const search = location.search;
    
    // Check if we're on a tickets page with a view parameter
    if (path === '/tickets' && search) {
      return `/tickets${search}`;
    }

    if (path === '/equipment') {
      const params = new URLSearchParams(search);
      if (params.get('tab') === 'replacements') {
        return '/replacements';
      }
    }
    
    return path;
  };

  // Get open submenu keys
  const getOpenKeys = () => {
    if (location.pathname.startsWith('/tickets')) {
      return ['/tickets'];
    }
    return [];
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {!hideAppSidebarOnMobile && (
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        {/* Logo */}
        <div
          style={{
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 16px',
            background: 'rgba(255, 255, 255, 0.05)',
          }}
        >
          {!collapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 180, height: 100, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, overflow: 'hidden' }}>
                {logoUrl && !logoError ? (
                  <img 
                    key={logoUrl}
                    src={logoUrl} 
                    alt="Vitreous Support Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    onError={(e) => {
                      console.error('[Layout] Failed to load logo from:', logoUrl);
                      setLogoError(true);
                    }}
                    onLoad={() => {
                      console.log('[Layout] Logo loaded successfully from:', logoUrl);
                    }}
                  />
                ) : (
                  <div style={{ fontSize: 48, fontWeight: 'bold', color: '#1976d2' }}>VS</div>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                display: 'flex',
                background: '#fff',
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {logoUrl && !logoError ? (
                <img 
                  key={logoUrl}
                  src={logoUrl} 
                  alt="Vitreous Support Logo" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  onError={(e) => {
                    console.error('[Layout] Failed to load logo from:', logoUrl);
                    setLogoError(true);
                  }}
                  onLoad={() => {
                    console.log('[Layout] Logo loaded successfully from:', logoUrl);
                  }}
                />
              ) : (
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2' }}>VS</div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
        />

        {/* Version Info - positioned above collapse button */}
        {versionInfo && !collapsed && (
          <div
            style={{
              position: 'absolute',
              bottom: 48,
              left: 0,
              right: 0,
              padding: '8px 16px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              background: 'rgba(0, 0, 0, 0.2)',
              textAlign: 'center',
            }}
          >
            <Text style={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.45)' }}>
              v{versionInfo.version}
            </Text>
          </div>
        )}
      </Sider>
      )}

      <AntLayout style={{ marginLeft: hideAppSidebarOnMobile ? 0 : (collapsed ? 80 : 200), transition: 'all 0.2s' }}>
        {/* Header */}
        <Header
          style={{
            padding: hideAppSidebarOnMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            gap: 16,
          }}
        >
          <div>
            {!hideAppSidebarOnMobile && React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'trigger',
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: 18, cursor: 'pointer' },
            })}
            {hideAppSidebarOnMobile && (
              <span style={{ fontSize: 16, fontWeight: 600, color: '#1976d2' }}>Chat</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'center', minWidth: 0 }}>
            <Search
              placeholder="Search tickets, businesses, users..."
              enterButton={<SearchOutlined />}
              size="middle"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={handleGlobalSearch}
              style={{ flex: 1, maxWidth: 400, minWidth: 200 }}
              allowClear
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="default"
              icon={<MessageOutlined />}
              onClick={() => window.open('/chat', '_blank', 'noopener,noreferrer')}
            >
              Chat
            </Button>
            {!hideAppSidebarOnMobile && <NotificationBell user={user} />}
            <Dropdown menu={{ items: profileMenu }} placement="bottomRight">
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text strong>
                  {user?.first_name} {user?.last_name}
                </Text>
                <Avatar src={user?.avatar_url} style={{ backgroundColor: '#1976d2' }}>
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </Avatar>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Content - no outer scroll on chat page so conversation list stays fixed */}
        <Content
          style={{
            margin: 0,
            padding: 0,
            minHeight: 'calc(100vh - 64px)',
            height: location.pathname === '/chat' ? 'calc(100vh - 64px)' : undefined,
            overflow: location.pathname === '/chat' ? 'hidden' : 'auto',
            background: '#fff',
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default LayoutComponent;
